import React, { useState, useEffect, useCallback } from 'react';
import { Play, Timer as TimerIcon } from 'lucide-react';
import { MatchState, MatchAction, Player } from '../../types';
import { TIMERS } from '../../constants';
import { ColorPicker, Timer, AnnouncementBanner, PlayerAvatar } from '../atoms';
import { formatRoundType } from '../../utils';
import { QuizModal } from './QuizModal';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useWakeLock } from '../../hooks/useWakeLock';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { enterFullscreenIfPreferred } from '../../utils/fullscreen';

interface BeforeMatchProps {
  matchState: MatchState;
  dispatch: React.Dispatch<MatchAction>;
  wakeLock: ReturnType<typeof useWakeLock>;
}

export function BeforeMatch({ matchState, dispatch, wakeLock }: BeforeMatchProps) {
  const { t } = useTranslation();
  const announcements = useAnnouncements();

  const [colorPicker, setColorPicker] = useState({
    isOpen: false,
    player: null as Player | null
  });
  const [warmupTimer, setWarmupTimer] = useState(false);
  const [serverSelection, setServerSelection] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  // Fermer la sélection du serveur avec Escape
  useEffect(() => {
    if (!serverSelection) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setServerSelection(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [serverSelection]);

  // Timer announcement callbacks for warmup
  const handleWarmupHalftime = useCallback(() => {
    announcements.showAnnouncement('timer_halftime', [t('announcements.timer.halftime')]);
  }, [announcements, t]);

  const handleWarmupFifteenSeconds = useCallback(() => {
    announcements.showAnnouncement('timer_fifteen_seconds', [t('announcements.timer.fifteen_seconds')]);
  }, [announcements, t]);

  const handleWarmupTime = useCallback(() => {
    announcements.showAnnouncement('timer_time', [t('announcements.timer.time')]);
  }, [announcements, t]);
  
  // Wake Lock: activer pendant le warmup timer pour éviter la mise en veille
  useEffect(() => {
    if (wakeLock.isSupported && warmupTimer) {
      wakeLock.request();
    } else if (!warmupTimer && wakeLock.isActive) {
      wakeLock.release();
    }
  }, [wakeLock, warmupTimer]);

  // Broadcast warmup timer to live display
  useEffect(() => {
    if (warmupTimer) {
      dispatch({
        type: 'SET_ACTIVE_TIMER',
        timer: {
          id: 'warmup',
          durationSeconds: TIMERS.warmup.duration,
          startedAt: Math.floor(Date.now() / 1000)
        }
      });
    } else {
      dispatch({ type: 'SET_ACTIVE_TIMER', timer: null });
    }
  }, [warmupTimer, dispatch]);

  // Clear active timer on unmount (e.g. match starts while warmup is running)
  useEffect(() => {
    return () => {
      dispatch({ type: 'SET_ACTIVE_TIMER', timer: null });
    };
  }, [dispatch]);
  
  const handleColorSelect = (color: string) => {
    if (colorPicker.player) {
      dispatch({ type: 'SET_PLAYER_COLOR', player: colorPicker.player, color });
    }
    setColorPicker({ isOpen: false, player: null });
  };
  
  // Starting the warmup or the match is the gesture fullscreen needs (menu preference)
  const handleStartWarmup = () => {
    enterFullscreenIfPreferred();
    setWarmupTimer(true);
  };

  const handleStartMatch = () => {
    enterFullscreenIfPreferred();
    setServerSelection(true);
  };
  
  const handleServerSelect = (player: Player) => {
    dispatch({ type: 'SET_SERVING_PLAYER', player });
    dispatch({ type: 'START_MATCH' });
  };

  const playerAName = matchState.matchInfo.playerA;
  const playerBName = matchState.matchInfo.playerB;
  
  return (
    <div className="min-h-screen p-4 relative">
      <div className="max-w-md mx-auto md:max-w-none md:mx-0">
        {/* Header */}
        <div className="bg-white p-4 mb-4">
          {/* Ligne 1 : Competition, Category, Draw */}
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div className="font-semibold text-sm">{matchState.matchInfo.competitionName}</div>
            <div className="font-semibold text-sm">{matchState.matchInfo.categoryName}</div>
            <div className="font-semibold text-sm">{matchState.matchInfo.drawName}</div>
          </div>

          {/* Ligne 2 : Referee, Court, Round type, Scoring type */}
          <div className="grid grid-cols-4 gap-4 mb-3">
            <div className="text-gray-600 text-sm">{matchState.matchInfo.refereeName}</div>
            <div className="text-gray-600 text-sm">{matchState.matchInfo.courtName}</div>
            <div className="text-gray-600 text-sm">
              {formatRoundType(t, matchState.matchInfo.roundType)}
            </div>
            <div className="text-gray-600 text-sm">{matchState.matchInfo.scoringType}</div>
          </div>

          {/* Ligne 3 : Date et heure (fusionnée et alignée à droite) */}
          <div className="text-right">
            <div className="text-gray-500 text-xs">{matchState.matchInfo.dateTime}</div>
          </div>
        </div>
        
        {/* Players */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <button
            onClick={() => setColorPicker({ isOpen: true, player: 'A' })}
            className="bg-white border border-gray-200 pt-3 px-4 pb-0 h-full flex flex-col items-center gap-2 min-w-0 overflow-hidden hover:bg-gray-50 transition-colors"
          >
            <PlayerAvatar
              avatarUrl={matchState.matchInfo.playerAAvatar}
              color={matchState.playerAColor}
              size={70}
              ringWidth={5}
              alt={matchState.matchInfo.playerA}
            />
            <span className="font-semibold text-base sm:text-lg text-gray-900 text-center break-words max-w-full">{matchState.matchInfo.playerA}</span>
            <div className="self-stretch -mx-4 mt-auto h-1.5" style={{ backgroundColor: matchState.playerAColor }} />
          </button>
          <button
            onClick={() => setColorPicker({ isOpen: true, player: 'B' })}
            className="bg-white border border-gray-200 pt-3 px-4 pb-0 h-full flex flex-col items-center gap-2 min-w-0 overflow-hidden hover:bg-gray-50 transition-colors"
          >
            <PlayerAvatar
              avatarUrl={matchState.matchInfo.playerBAvatar}
              color={matchState.playerBColor}
              size={70}
              ringWidth={5}
              alt={matchState.matchInfo.playerB}
            />
            <span className="font-semibold text-base sm:text-lg text-gray-900 text-center break-words max-w-full">{matchState.matchInfo.playerB}</span>
            <div className="self-stretch -mx-4 mt-auto h-1.5" style={{ backgroundColor: matchState.playerBColor }} />
          </button>
        </div>

        {/* Séparateur */}
        <hr className="my-4 border-gray-300" />

        {/* Action buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={handleStartWarmup}
            className="bg-gray-600 text-white py-4 font-semibold"
          >
            <div className="flex items-center justify-center gap-2">
             <TimerIcon size={20} />
             {t('screens.before.warmupTimer')}
           </div>
          </button>
          <button
            onClick={handleStartMatch}
            className="bg-green-600 text-white py-4 font-semibold"
          >
            <div className="flex items-center justify-center gap-2">
             <Play size={20} />
             {t('screens.before.startMatch')}
           </div>
          </button>
        </div>
      </div>
      
      {/* Color Picker Modal */}
      <ColorPicker
        isOpen={colorPicker.isOpen}
        currentColor={colorPicker.player === 'A' ? matchState.playerAColor : matchState.playerBColor}
        onColorSelect={handleColorSelect}
        onClose={() => setColorPicker({ isOpen: false, player: null })}
      />
      
      {/* Warmup Timer */}
      <Timer
        isOpen={warmupTimer}
        title={t('screens.before.warmupTimer')}
        duration={TIMERS.warmup.duration}
        compact={showQuiz}
        onClose={() => { setWarmupTimer(false); setShowQuiz(false); }}
        showHalftime={TIMERS.warmup.showHalftime}
        showQuiz={TIMERS.warmup.showQuiz}
        onOpenQuiz={() => setShowQuiz(true)}
        onHalftime={handleWarmupHalftime}
        onFifteenSeconds={handleWarmupFifteenSeconds}
        onTime={handleWarmupTime}
        onPause={() => dispatch({ type: 'PAUSE_TIMER' })}
        onResume={() => dispatch({ type: 'RESUME_TIMER' })}
      />

      <QuizModal isOpen={showQuiz} onClose={() => setShowQuiz(false)} hasCompactTimer={warmupTimer} />

      {/* Announcement Banner */}
      <AnnouncementBanner
        announcement={announcements.currentAnnouncement}
        onDismiss={announcements.dismissAnnouncement}
      />
      
      {/* Server Selection Modal - Layout adaptatif */}
      {serverSelection && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="server-selection-title"
          onClick={() => setServerSelection(false)}
        >
          <div className="bg-white p-6 max-w-md mx-4 w-full" onClick={(e) => e.stopPropagation()}>
            <h3 id="server-selection-title" className="text-lg font-semibold mb-6 text-center">{t('screens.before.whoServes')}</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleServerSelect('A')}
                className="bg-white border border-gray-200 pt-6 px-3 pb-0 h-full flex flex-col items-center gap-2 min-w-0 overflow-hidden hover:bg-gray-50 transition-colors"
                title={playerAName}
              >
                <PlayerAvatar
                  avatarUrl={matchState.matchInfo.playerAAvatar}
                  color={matchState.playerAColor}
                  size={56}
                  ringWidth={4}
                  alt={playerAName}
                />
                <span className="font-semibold text-base text-gray-900 text-center break-words leading-tight max-w-full">
                  {playerAName}
                </span>
                <div className="self-stretch -mx-3 mt-auto h-1.5" style={{ backgroundColor: matchState.playerAColor }} />
              </button>
              <button
                onClick={() => handleServerSelect('B')}
                className="bg-white border border-gray-200 pt-6 px-3 pb-0 h-full flex flex-col items-center gap-2 min-w-0 overflow-hidden hover:bg-gray-50 transition-colors"
                title={playerBName}
              >
                <PlayerAvatar
                  avatarUrl={matchState.matchInfo.playerBAvatar}
                  color={matchState.playerBColor}
                  size={56}
                  ringWidth={4}
                  alt={playerBName}
                />
                <span className="font-semibold text-base text-gray-900 text-center break-words leading-tight max-w-full">
                  {playerBName}
                </span>
                <div className="self-stretch -mx-3 mt-auto h-1.5" style={{ backgroundColor: matchState.playerBColor }} />
              </button>
            </div>

            <button
              onClick={() => setServerSelection(false)}
              className="w-full mt-4 bg-gray-500 text-white py-2 hover:bg-gray-600 transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}